import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  useTheme,
  Avatar,
  Chip,
  Divider,
  Grid,
  Alert,
  IconButton,
  Modal,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import {
  ArrowBack,
  Inventory,
  MiscellaneousServices,
  PictureAsPdf,
  Star,
  Person,
  ShareOutlined,
  Close as CloseIcon,
  Link as CopyLinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  ContactMail,
} from '@mui/icons-material';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import { ServiceProduct } from '@/constants/types';
import { serviceProductService } from '@/services/business/serviceProductService';
import { enquiryService } from '@/services/business/enquiryService';
import { getFileUrl } from '@/utils/businessHelpers';
import toast from 'react-hot-toast';

// Define types for populated business and user info
interface PopulatedBusinessInfo {
  _id: string;
  businessName?: string;
  logoId?: string;
  email?: string;
  contactNumber?: string;
  whatsappNumber?: string;
}

interface PopulatedUserInfo {
  _id: string;
  name?: string;
  email?: string;
  profileImage?: string;
}

export const ServiceProductDetailPage: React.FC = () => {
  const { businessId, serviceProductId } = useParams<{ businessId?: string; serviceProductId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [product, setProduct] = useState<ServiceProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isEnquiring, setIsEnquiring] = useState(false);
  const [enquired, setEnquired] = useState(false);

  const trackEnquiry = async () => {
    if (!product?._id) return;
    try {
      await enquiryService.track(product._id);
      setEnquired(true);
    } catch {
      // apiClient already surfaces toast on error
    }
  };

  const handleEnquire = async () => {
    if (!product?._id || isEnquiring || enquired) return;
    try {
      setIsEnquiring(true);
      await trackEnquiry();
      toast.success('Enquiry sent. The business will reach out to you shortly.');
    } finally {
      setIsEnquiring(false);
    }
  };

  const handleWhatsAppEnquire = () => {
    const number = (product?.businessId as PopulatedBusinessInfo | undefined)?.whatsappNumber;
    if (!number) {
      toast.error('WhatsApp number not available');
      return;
    }
    const message = `Hi, I'm interested in "${product?.title}". Please share more details.`;
    window.open(`https://wa.me/91${number}?text=${encodeURIComponent(message)}`, '_blank');
    trackEnquiry();
  };

  const handleDownloadBrochure = () => {
    const url = product?.brochure?.url;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    trackEnquiry();
  };

  useEffect(() => {
    if (serviceProductId) {
      loadServiceProduct();
    }
  }, [serviceProductId]);

  const loadServiceProduct = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await serviceProductService.getServiceProduct(serviceProductId!);
      setProduct(response.data);
    } catch (err: any) {
      console.error('Failed to load product:', err);
      setError(err?.message || 'Failed to load product details');
    } finally {
      setIsLoading(false);
    }
  };

  // Extract business info from populated businessId
  const getBusinessInfo = (): PopulatedBusinessInfo | null => {
    if (!product?.businessId) return null;
    if (typeof product.businessId === 'string') {
      return { _id: product.businessId };
    }
    return product.businessId as PopulatedBusinessInfo;
  };

  // Extract user info from populated userId
  const getUserInfo = (): PopulatedUserInfo | null => {
    if (!product?.userId) return null;
    if (typeof product.userId === 'string') {
      return { _id: product.userId };
    }
    return product.userId as PopulatedUserInfo;
  };

  const businessInfo = getBusinessInfo();
  const userInfo = getUserInfo();

  // Share functionality
  const shareUrl = product ? `https://hrtalky.in/products/${product._id}` : '';
  const shareTitle = product?.title || 'Check out this product';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarOpen(true);
      setShareOpen(false);
    });
  };

  const socialIcons = [
    { icon: <CopyLinkIcon />, label: 'Copy link', onClick: handleCopyLink },
    { component: FacebookShareButton, icon: <FacebookIcon />, label: 'Facebook', props: { hashtag: '#HRTalky' } },
    { component: TwitterShareButton, icon: <TwitterIcon />, label: 'X', props: { title: shareTitle, hashtags: ['HRTalky', 'Business'] } },
    { component: LinkedinShareButton, icon: <LinkedInIcon />, label: 'LinkedIn', props: { title: shareTitle, source: 'HRTalky' } },
    { component: WhatsappShareButton, icon: <WhatsAppIcon />, label: 'WhatsApp', props: { title: `${shareTitle}\n\n${shareUrl}`, separator: '' } },
  ];

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Skeleton width={100} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton width="60%" height={40} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={24} sx={{ mb: 3 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton width="80%" height={20} />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back
        </Button>
        <Alert severity="error">{error || 'Product not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Back Button & Share */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            if (businessId) {
              // Came from business page
              navigate(`/businesses/${businessId}`, { state: { activeTab: 1 } });
            } else if (businessInfo?._id) {
              // Came from explore page, go to business page with products tab
              navigate(`/businesses/${businessInfo._id}`, { state: { activeTab: 1 } });
            } else {
              // Fallback to explore page
              navigate('/products-services');
            }
          }}
          sx={{ textTransform: 'none' }}
        >
          Back to Products & Services
        </Button>
        <IconButton onClick={() => setShareOpen(true)} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <ShareOutlined fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          overflow: 'hidden',
        }}
      >
        <Grid container>
          {/* Left Column - Product Image */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 280, sm: 360, md: '100%' },
                minHeight: { md: 520 },
                bgcolor: 'background.paper',
                borderRight: { md: `1px solid ${theme.palette.divider}` },
                borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                overflow: 'hidden',
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
              }}
            >
              {product.productImage?.url ? (
                <Box
                  component="img"
                  src={product.productImage.url}
                  alt={product.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.4s ease',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}11 0%, ${theme.palette.primary.main}22 100%)`,
                  }}
                >
                  {product.serviceType === 'product' ? (
                    <Inventory sx={{ fontSize: 120, color: 'text.disabled', opacity: 0.5 }} />
                  ) : (
                    <MiscellaneousServices sx={{ fontSize: 120, color: 'text.disabled', opacity: 0.5 }} />
                  )}
                </Box>
              )}

              {/* Promoted Badge */}
              {product.isPromoted && (
                <Chip
                  icon={<Star sx={{ fontSize: 14 }} />}
                  label="Promoted"
                  size="small"
                  color="warning"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    boxShadow: theme.shadows[4],
                  }}
                />
              )}
            </Box>
          </Grid>

          {/* Right Column - Product Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: { xs: 3, sm: 4 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            {/* Type Chip */}
            <Chip
              icon={product.serviceType === 'product' ? <Inventory sx={{ fontSize: 16 }} /> : <MiscellaneousServices sx={{ fontSize: 16 }} />}
              label={product.serviceType === 'product' ? 'Product' : 'Service'}
              size="small"
              color={product.serviceType === 'product' ? 'primary' : 'secondary'}
              sx={{
                alignSelf: 'flex-start',
                mb: 2,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
              }}
            />

            {/* Title */}
            <Typography
              variant="h3"
              sx={{
                mb: 2,
                fontWeight: 800,
                letterSpacing: -1,
                lineHeight: 1.1,
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
              }}
            >
              {product.title}
            </Typography>

            {/* Price */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 3 }}>
              {product.price !== undefined && product.price > 0 ? (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    ₹
                  </Typography>
                  <Typography
                    variant="h3"
                    color="primary"
                    sx={{ fontWeight: 800, letterSpacing: -1, lineHeight: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}
                  >
                    {product.price.toLocaleString('en-IN')}
                  </Typography>
                </>
              ) : (
                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                  Price on Request
                </Typography>
              )}
            </Box>

            {/* Description */}
            {product.description && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 1 }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 3,
                    lineHeight: 1.85,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {product.description}
                </Typography>
              </>
            )}

            {/* CTAs */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, justifyContent: 'center' }}>
              {businessInfo?.whatsappNumber && (
                <Button
                  onClick={handleWhatsAppEnquire}
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1.1,
                    borderRadius: 2,
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#1ebe57' },
                  }}
                >
                  WhatsApp Enquire
                </Button>
              )}
              {product.brochure?.url && (
                <Button
                  onClick={handleDownloadBrochure}
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1.1,
                    borderRadius: 2,
                  }}
                >
                  Download Brochure
                </Button>
              )}
            </Box>

            {/* Spacer to push footer down */}
            <Box sx={{ flexGrow: 1 }} />
     <Button
                onClick={handleEnquire}
                disabled={isEnquiring || enquired}
                variant="contained"
                startIcon={enquired ? <CheckIcon /> : <ContactMail />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  letterSpacing: 0.3,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: theme.shadows[4] },
                }}
              >
                {enquired ? 'Enquiry Sent' : isEnquiring ? 'Sending…' : 'Enquire Now'}
              </Button>
            {/* Business Info */}
            {/* {businessInfo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  onClick={() => window.open(`/businesses/${businessInfo._id}`, '_blank')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    p: 1,
                    mx: -1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Avatar
                    src={businessInfo.logoId ? getFileUrl(businessInfo.logoId) : undefined}
                    sx={{
                      width: 52,
                      height: 52,
                      bgcolor: theme.palette.primary.main,
                      fontWeight: 700,
                    }}
                  >
                    {businessInfo.businessName ? businessInfo.businessName.charAt(0).toUpperCase() : <BusinessIcon />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}
                    >
                      Business
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.3 }}>
                      {businessInfo.businessName || 'View Business'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.25 }}>
                      {businessInfo.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Email sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {businessInfo.email}
                          </Typography>
                        </Box>
                      )}
                      {businessInfo.contactNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary">
                            {businessInfo.contactNumber}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <OpenInNew sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                </Box>
              </>
            )} */}

            {/* Created By */}
            {userInfo && userInfo.name && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    src={userInfo.profileImage ? getFileUrl(userInfo.profileImage) : undefined}
                    sx={{ width: 32, height: 32 }}
                  >
                    {userInfo.name?.charAt(0).toUpperCase() || <Person />}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, lineHeight: 1.2 }}
                    >
                      Listed by
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {userInfo.name}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Share Modal */}
      <Modal open={shareOpen} onClose={() => setShareOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 340, bgcolor: 'background.paper',
          boxShadow: 24, p: 3, borderRadius: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Share Product</Typography>
            <IconButton size="small" onClick={() => setShareOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box>
            {socialIcons.map((social, i) => {
              const rowSx = {
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, borderRadius: 2, mb: 0.5,
                cursor: 'pointer', width: '100%',
                '&:hover': { bgcolor: 'action.hover' },
              };
              if (social.onClick) {
                return (
                  <Box key={i} onClick={social.onClick} sx={rowSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </Box>
                );
              }
              const ShareBtn = social.component!;
              return (
                <Box key={i} sx={{ ...rowSx, p: 0 }}>
                  <ShareBtn url={shareUrl} {...(social.props || {})} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', width: '100%', color: 'inherit', textDecoration: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </ShareBtn>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>

      {/* Copy snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2 }}
          message={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckIcon /><Typography variant="body2" sx={{ color: 'white' }}>Link copied!</Typography></Box>}
          action={<IconButton onClick={() => setSnackbarOpen(false)} color="inherit" size="small"><CloseIcon fontSize="small" /></IconButton>}
        />
      </Snackbar>
    </Box>
  );
};
